import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Loader2, CalendarHeart, MapPin, Clock, Check, Users, Baby, Minus, Plus, X, Snowflake,
  Coffee, Wine, TreePine, Gift, Sparkles, Music2, Ticket, HandHeart, UtensilsCrossed,
  ArrowRight, ChevronDown, Heart, Dumbbell,
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import './Inauguration.css';

const PINE = '#3E5540';
const DEFAULT_TARGET = '2026-12-01T00:00:00'; // décompte jusqu'à décembre 2026 (date exacte communiquée par l'admin)

const EXPERIENCES = [
  { tag: 'Café & douceurs', title: 'Le Coffee Truck Vintage', img: '/assets/event-coffee-truck.webp',
    text: "Un camion vintage décoré pour Noël : café, matcha, chocolat chaud, jus détox et gourmandises à savourer." },
  { tag: 'Dégustation', title: "L'Espace Dégustation", img: '/assets/event-wine-barrels.webp',
    text: "Un stand élégant de dégustation de vin, tonneaux et matières naturelles, lanternes et sapins illuminés." },
  { tag: 'Rencontres', title: 'Le Chalet PURO', img: '/assets/event-chalet-tent.webp',
    text: "Une grande tente blanche aux voilages écrus, lumières chaleureuses et mobilier élégant — un espace d'échanges et de rencontres." },
  { tag: 'Studio', title: 'La Découverte du Studio', img: '/assets/event-studio-discovery.webp',
    text: "Visite du studio, découverte des reformers, présentation des coachs et de l'univers PURO." },
];

const GIFTS = [
  { icon: Dumbbell, label: 'Accessoires Pilates' },
  { icon: Sparkles, label: 'Produits exclusifs PURO' },
  { icon: HandHeart, label: 'Tapis de yoga' },
  { icon: Ticket, label: 'Séances offertes' },
  { icon: Gift, label: 'Cartes cadeaux' },
  { icon: Heart, label: 'Surprises de nos partenaires' },
];

const PROGRAM = [
  { icon: HandHeart, title: 'Rencontre avec les fondateurs', text: "Un moment privilégié pour échanger avec celles et ceux qui ont imaginé PURO." },
  { icon: Users, title: 'Présentation de nos coachs', text: "Découvrez l'équipe qui vous accompagnera au quotidien." },
  { icon: TreePine, title: 'Le studio en avant-première', text: "Une visite guidée des espaces et des reformers avant l'ouverture." },
  { icon: UtensilsCrossed, title: 'Buffet & gourmandises', text: "Un buffet raffiné à partager dans une ambiance chaleureuse." },
  { icon: Wine, title: 'Dégustation de vin', text: "Une sélection et des accords gourmands, pour les adultes, avec modération." },
  { icon: Coffee, title: 'Coffee truck & boissons chaudes', text: "Cafés, chocolats chauds et douceurs tout au long de la journée." },
  { icon: Music2, title: 'Animations & surprises', text: "Des instants à vivre et à partager, pensés pour vous." },
  { icon: Gift, title: 'Tirage au sort de cadeaux', text: "Des lots exclusifs PURO et de nos partenaires à remporter." },
];

const ZONES = [
  'Tente blanche', 'Camion coffee shop', 'Stand dégustation de vin',
  "Calendrier de l'Avent XXL", 'Entrée du studio', 'Parking (grand espace)',
];

function useCountdown(targetISO) {
  const target = new Date(targetISO).getTime();
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  const diff = Math.max(0, target - now);
  const s = Math.floor(diff / 1000);
  return { days: Math.floor(s / 86400), hours: Math.floor((s % 86400) / 3600), minutes: Math.floor((s % 3600) / 60), seconds: s % 60 };
}

function Countdown({ targetISO }) {
  const c = useCountdown(targetISO);
  const units = [{ v: c.days, l: 'Jours' }, { v: c.hours, l: 'Heures' }, { v: c.minutes, l: 'Minutes' }, { v: c.seconds, l: 'Secondes' }];
  return (
    <div className="cd-row" data-testid="event-countdown">
      {units.map((u) => (
        <div key={u.l} className="flex flex-col items-center">
          <div className="cd-cell"><span className="cd-num" data-testid={`cd-${u.l.toLowerCase()}`}>{String(u.v).padStart(2, '0')}</span></div>
          <span className="cd-lbl">{u.l}</span>
        </div>
      ))}
    </div>
  );
}

function Stepper({ label, icon: Icon, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between bg-[#F4ECDF] rounded-2xl px-4 py-3">
      <span className="flex items-center gap-2 text-sm text-[#43301F]"><Icon className="w-4 h-4" style={{ color: PINE }} />{label}</span>
      <div className="flex items-center gap-3">
        <button type="button" data-testid={`${testid}-minus`} onClick={() => onChange(Math.max(0, value - 1))} className="w-8 h-8 rounded-full bg-white border border-[#E7DCCB] flex items-center justify-center text-[#43301F] hover:bg-[#E9DDCC] transition-colors"><Minus className="w-4 h-4" /></button>
        <span className="w-6 text-center text-sm font-semibold text-[#43301F]" data-testid={`${testid}-value`}>{value}</span>
        <button type="button" data-testid={`${testid}-plus`} onClick={() => onChange(value + 1)} className="w-8 h-8 rounded-full bg-white border border-[#E7DCCB] flex items-center justify-center text-[#43301F] hover:bg-[#E9DDCC] transition-colors"><Plus className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

export default function Inauguration() {
  const { isAuthenticated, user } = useAuth();
  const isClient = user && user.role === 'client';
  const [data, setData] = useState(null);
  const [guests, setGuests] = useState(0);
  const [children, setChildren] = useState(0);
  const [guestsNames, setGuestsNames] = useState('');
  const [note, setNote] = useState('');
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);

  const heroBg = useRef(null);
  const treeL = useRef(null);
  const treeR = useRef(null);
  const treeS = useRef(null);

  const load = () => axios.get(`${API}/event`, { withCredentials: true })
    .then((r) => {
      setData(r.data);
      const m = r.data.my_registration;
      if (m) { setGuests(m.guests_count || 0); setChildren(m.children_count || 0); setGuestsNames(m.guests_names || ''); setNote(m.note || ''); setConsent(true); }
    })
    .catch(() => setData({ config: null }));

  useEffect(() => { load(); }, [isAuthenticated]); // eslint-disable-line

  // Reveal on scroll
  useEffect(() => {
    if (!data) return;
    const els = document.querySelectorAll('.reveal');
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      els.forEach((e) => e.classList.add('is-visible')); return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); } });
    }, { threshold: 0.14 });
    els.forEach((e) => io.observe(e));
    return () => io.disconnect();
  }, [data]);

  // Parallax
  useEffect(() => {
    if (!data) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        if (heroBg.current) heroBg.current.style.transform = `translateY(${y * 0.22}px) scale(1.02)`;
        if (treeL.current) treeL.current.style.transform = `translateY(${y * -0.08}px)`;
        if (treeR.current) treeR.current.style.transform = `translateY(${y * -0.12}px)`;
        if (treeS.current) treeS.current.style.transform = `translateY(${y * -0.16}px)`;
        raf = null;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [data]);

  const submit = async () => {
    if (!data?.my_registration && !consent) { toast.error("Merci d'accepter les conditions et la politique de confidentialité."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/event/register`, { guests_count: guests, children_count: children, guests_names: guestsNames, note, consent: true }, { withCredentials: true });
      await load();
      toast.success(data?.my_registration ? 'Invitation mise à jour.' : 'Votre invitation est réservée !');
    } catch (e) { toast.error(e.response?.data?.detail || 'Une erreur est survenue.'); } finally { setSaving(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Annuler votre venue à l'inauguration ? Votre place sera libérée et proposée à la liste d'attente.")) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/event/register`, { withCredentials: true });
      setGuests(0); setChildren(0); setGuestsNames(''); setNote(''); setConsent(false);
      await load();
      toast.success('Invitation annulée.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Une erreur est survenue.'); } finally { setSaving(false); }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center bg-[#F7F2E9]"><Loader2 className="w-6 h-6 animate-spin" style={{ color: PINE }} /></div>;
  if (!data.config || !data.config.enabled) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F7F2E9] px-4" data-testid="event-disabled"><p className="text-[#7A6455] text-center">L'inauguration n'est pas encore ouverte. Revenez très bientôt !</p></div>;
  }

  const cfg = data.config;
  const stats = data.stats;
  const mine = data.my_registration;
  const target = cfg.event_date ? `${cfg.event_date}T10:00:00` : DEFAULT_TARGET;

  const renderRegistration = () => {
    if (!isAuthenticated) {
      return (
        <div className="text-center" data-testid="event-login-prompt">
          <p className="text-[#7A6455] mb-5">Les invitations sont réservées aux clientes PURO connectées.</p>
          <Link to="/login" data-testid="event-login-link" className="inline-flex items-center gap-2 text-white rounded-full px-7 py-3 text-sm font-medium transition-colors" style={{ backgroundColor: PINE }}>Se connecter pour réserver</Link>
        </div>
      );
    }
    if (!isClient) return <p className="text-center text-[#7A6455]" data-testid="event-notclient">Les invitations sont réservées aux clientes. Votre compte ne permet pas de vous inscrire ici.</p>;
    const form = (
      <div className="space-y-3">
        <Stepper label="Accompagnants (adultes)" icon={Users} value={guests} onChange={setGuests} testid="event-guests" />
        <Stepper label="Enfants" icon={Baby} value={children} onChange={setChildren} testid="event-children" />
        <textarea data-testid="event-guests-names" value={guestsNames} onChange={(e) => setGuestsNames(e.target.value)} rows={2} placeholder="Prénoms de vos accompagnants (facultatif)" className="w-full bg-[#F4ECDF] rounded-2xl px-4 py-3 text-sm text-[#43301F] placeholder:text-[#7A6455]/60 outline-none focus:ring-2 focus:ring-[#3E5540]/30" />
        <textarea data-testid="event-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Un mot pour l'équipe ? (facultatif)" className="w-full bg-[#F4ECDF] rounded-2xl px-4 py-3 text-sm text-[#43301F] placeholder:text-[#7A6455]/60 outline-none focus:ring-2 focus:ring-[#3E5540]/30" />
      </div>
    );
    if (mine) {
      return (
        <div data-testid="event-my-registration">
          <div className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm mb-5 ${mine.status === 'confirmed' ? 'text-white' : 'bg-[#C76542]/15 text-[#C76542]'}`} style={mine.status === 'confirmed' ? { backgroundColor: PINE } : {}}>
            {mine.status === 'confirmed' ? <><Check className="w-4 h-4" /> Invitation confirmée</> : <><Clock className="w-4 h-4" /> Liste d'attente · position {mine.position}</>}
          </div>
          <p className="text-sm text-[#7A6455] mb-5">Vous êtes inscrite avec <strong className="text-[#43301F]">{mine.guests_count}</strong> accompagnant(s) et <strong className="text-[#43301F]">{mine.children_count}</strong> enfant(s). Modifiez ou annulez votre venue ci-dessous.</p>
          {form}
          <div className="flex flex-col sm:flex-row gap-3 mt-5">
            <button data-testid="event-update-btn" onClick={submit} disabled={saving} className="flex-1 inline-flex items-center justify-center gap-2 text-white rounded-full px-6 py-3 text-sm font-medium transition-colors disabled:opacity-60" style={{ backgroundColor: PINE }}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Mettre à jour</button>
            <button data-testid="event-cancel-btn" onClick={cancel} disabled={saving} className="inline-flex items-center justify-center gap-2 bg-white border border-[#C76542]/40 text-[#C76542] rounded-full px-6 py-3 text-sm font-medium hover:bg-[#C76542]/10 transition-colors disabled:opacity-60"><X className="w-4 h-4" /> Annuler ma venue</button>
          </div>
        </div>
      );
    }
    return (
      <div data-testid="event-register-form">
        {stats.is_full && <div className="bg-[#C76542]/10 text-[#C76542] rounded-2xl px-4 py-3 text-sm mb-5" data-testid="event-full-notice">Les {stats.capacity} invitations principales sont réservées. Vous pouvez rejoindre la <strong>liste d'attente</strong> : nous vous préviendrons si une place se libère.</div>}
        <p className="text-sm text-[#7A6455] mb-5">Réservez votre invitation. Vous pouvez venir accompagnée : indiquez le nombre d'adultes et d'enfants qui vous accompagnent.</p>
        {form}
        <label className="flex items-start gap-3 mt-4 text-sm text-[#6B574A] cursor-pointer select-none">
          <input type="checkbox" data-testid="event-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#3E5540]" />
          <span>J'accepte les conditions de l'événement et la politique de confidentialité PURO.</span>
        </label>
        <button data-testid="event-submit-btn" onClick={submit} disabled={saving} className="w-full mt-5 inline-flex items-center justify-center gap-2 text-white rounded-full px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.1em] transition-colors disabled:opacity-60" style={{ backgroundColor: PINE }}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}{stats.is_full ? "Rejoindre la liste d'attente" : 'Je réserve mon invitation'}
        </button>
      </div>
    );
  };

  return (
    <div className="inaug min-h-screen pb-24" data-testid="inauguration-page">
      {/* ============ HERO ============ */}
      <section className="inaug-hero">
        <img ref={heroBg} src="/assets/event-hero-facade.webp" alt="Le studio PURO décoré pour l'inauguration d'hiver" className="inaug-hero__bg" fetchpriority="high" />
        <div className="inaug-hero__veil" />
        <div className="snow" aria-hidden="true">
          {Array.from({ length: 26 }).map((_, i) => {
            const size = 2 + (i % 4);
            const left = (i * 37) % 100;
            const dur = 9 + (i % 7) * 1.6;
            const delay = (i % 10) * 0.9;
            return <i key={i} style={{ left: `${left}%`, width: size, height: size, animationDuration: `${dur}s`, animationDelay: `${delay}s` }} />;
          })}
        </div>

        <div className="inaug-hero__inner">
          <p className="inaug-kicker"><Snowflake className="w-3.5 h-3.5" /> Save the date <Snowflake className="w-3.5 h-3.5" /></p>
          <h1 className="inaug-h1" data-testid="event-title">
            <span className="sub sub--solo">L'Inauguration</span>
          </h1>
          <p className="inaug-lede" data-testid="event-tagline">Un hiver. Un lieu. Une nouvelle expérience.</p>
          <p className="inaug-when" data-testid="event-date">{cfg.date_label} — Magalas</p>
          <p className="inaug-quote">« Une nouvelle histoire s'apprête à commencer. Et si vous en découvriez les premiers instants avec nous ? »</p>
          <div className="mt-8"><Countdown targetISO={target} /></div>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-9">
            <a href="#experience" className="inaug-cta" data-testid="event-hero-cta">Découvrir l'inauguration <ArrowRight className="w-4 h-4" /></a>
            <a href="#invitation" className="inaug-cta inaug-cta--ghost" data-testid="event-hero-cta2">Réserver mon invitation</a>
          </div>
        </div>
        <a href="#experience" aria-label="Défiler" className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-white/80 animate-bounce"><ChevronDown className="w-6 h-6" /></a>
      </section>

      {/* ============ EXPÉRIENCE ============ */}
      <section id="experience" className="max-w-6xl mx-auto px-5 pt-24 scroll-mt-20" data-testid="event-experience">
        <div className="text-center reveal">
          <p className="inaug-eyebrow justify-center">L'expérience</p>
          <h2 className="font-heading text-3xl sm:text-5xl text-[#43301F] mt-4">Une expérience à découvrir</h2>
          <p className="text-[#6B574A] max-w-2xl mx-auto mt-4">Un véritable village d'hiver installé en plein air, sur le grand parvis du studio. Chaque espace a été pensé comme une invitation.</p>
        </div>
        <div className="mt-16 space-y-16 sm:space-y-24">
          {EXPERIENCES.map((x, i) => (
            <div key={x.title} className={`grid md:grid-cols-2 gap-8 md:gap-14 items-center ${i % 2 === 1 ? 'md:[&>*:first-child]:order-2' : ''}`} data-testid={`xp-item-${i}`}>
              <div className="xp-img reveal aspect-[4/3]">
                <span className="xp-tag">{x.tag}</span>
                <img src={x.img} alt={x.title} loading="lazy" />
              </div>
              <div className={`reveal reveal-d1 ${i % 2 === 1 ? 'md:text-right' : ''}`}>
                <span className="inaug-eyebrow">0{i + 1}</span>
                <h3 className="font-heading text-2xl sm:text-4xl text-[#43301F] mt-4 mb-4">{x.title}</h3>
                <p className="text-[#6B574A] leading-relaxed text-[17px]">{x.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ SURPRISES — CALENDRIER DE L'AVENT ============ */}
      <section className="relative mt-28 py-20 overflow-hidden" data-testid="event-surprises">
        <img src="/assets/logo-puro.png" alt="" aria-hidden="true" className="inaug-watermark" style={{ top: 30, right: -40 }} />
        <img src="/assets/event-tree-small.png" alt="" aria-hidden="true" className="hidden md:block absolute left-0 bottom-0 w-36 float-slow opacity-90" />
        <div className="max-w-5xl mx-auto px-5">
          <div className="text-center reveal">
            <p className="inaug-eyebrow justify-center">Les surprises de Noël</p>
            <h2 className="font-heading text-3xl sm:text-5xl text-[#43301F] mt-4">25 invitations. Des surprises à découvrir.</h2>
            <p className="text-[#6B574A] max-w-2xl mx-auto mt-4">Un grand calendrier de l'Avent XXL rythme la journée. Derrière chaque case, une attention imaginée pour vous. Les cadeaux restent secrets jusqu'au jour J.</p>
          </div>
          <div className="advent mt-12 max-w-2xl mx-auto reveal reveal-d1" data-testid="advent-calendar">
            {Array.from({ length: 25 }).map((_, i) => (
              <button type="button" key={i} className="advent-door" data-testid={`advent-door-${i + 1}`} aria-label={`Case ${i + 1}`}>
                <span className="num">{i + 1}</span>
                <span className="spark"><Sparkles className="w-5 h-5" /></span>
              </button>
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-3 mt-12 reveal reveal-d2" data-testid="gift-list">
            {GIFTS.map((g) => (
              <span key={g.label} className="gift-chip"><g.icon className="w-4 h-4" style={{ color: PINE }} />{g.label}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PROGRAMME ============ */}
      <section className="max-w-6xl mx-auto px-5 mt-20" data-testid="event-programme">
        <div className="text-center reveal">
          <p className="inaug-eyebrow justify-center">Le programme</p>
          <h2 className="font-heading text-3xl sm:text-5xl text-[#43301F] mt-4">Bien plus qu'une inauguration.</h2>
          <p className="font-heading text-2xl sm:text-3xl text-[#8a7461] italic mt-1">Une expérience à partager.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
          {PROGRAM.map((p, i) => (
            <div key={p.title} className={`prog-card reveal reveal-d${(i % 3) + 1}`} data-testid={`program-${i}`}>
              <div className="prog-icn"><p.icon className="w-5 h-5" /></div>
              <h3 className="font-heading text-xl text-[#43301F] mb-2">{p.title}</h3>
              <p className="text-sm text-[#6B574A] leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ LE LIEU ============ */}
      <section className="max-w-5xl mx-auto px-5 mt-24 reveal" data-testid="event-lieu">
        <div className="bg-white/70 backdrop-blur border border-[#E7DCCB] rounded-3xl p-8 sm:p-12 text-center">
          <TreePine className="w-6 h-6 mx-auto" style={{ color: PINE }} />
          <h2 className="font-heading text-2xl sm:text-4xl text-[#43301F] mt-4">Un lieu, une communauté, une même énergie</h2>
          <p className="text-[#6B574A] max-w-xl mx-auto mt-3">L'ensemble prend place en extérieur, sur le grand parvis et le parking du studio à Magalas.</p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {ZONES.map((z, i) => (
              <span key={z} className="inline-flex items-center gap-2.5 bg-[#F4ECDF] border border-[#E7DCCB] rounded-full pl-1.5 pr-4 py-1.5 text-sm text-[#43301F]">
                <span className="map-pin">{i + 1}</span>{z}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ============ INVITATION ============ */}
      <section id="invitation" className="max-w-xl mx-auto px-5 mt-24 scroll-mt-20" data-testid="event-inscription">
        <div className="text-center reveal mb-8">
          <p className="inaug-eyebrow justify-center">Réserver</p>
          <h2 className="font-heading text-3xl sm:text-5xl text-[#43301F] mt-4">Et si vous faisiez partie des premiers invités ?</h2>
          <p className="text-[#6B574A] mt-4 italic">« Nous avons imaginé cette inauguration comme un moment privilégié, à partager ensemble. Les invitations sont limitées. »</p>
        </div>
        <div className="resv-card reveal reveal-d1">
          <div className="resv-card__top" />
          <div className="p-6 sm:p-10">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 text-sm text-[#43301F] bg-[#F4ECDF] rounded-full px-4 py-1.5" data-testid="event-spots">
                <Users className="w-4 h-4" style={{ color: PINE }} />
                {stats.is_full ? <span>Complet — {stats.waitlist} en liste d'attente</span> : <span><strong>{stats.spots_left}</strong> invitation{stats.spots_left > 1 ? 's' : ''} sur {stats.capacity}</span>}
              </div>
              <div className="flex items-center justify-center gap-4 mt-4 text-xs text-[#8a7461]">
                <span className="inline-flex items-center gap-1.5"><CalendarHeart className="w-3.5 h-3.5" style={{ color: PINE }} />{cfg.date_label}</span>
                <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" style={{ color: PINE }} />{cfg.location}</span>
              </div>
            </div>
            {renderRegistration()}
            <p className="text-center text-xs text-[#a08f7e] mt-6">{stats.capacity} inscriptions principales · accompagnants et enfants illimités en plus.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
